import { queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	exportPreview,
	getEvent,
	getResolutionSource,
	listAttendees,
	listCtas,
	listEvents,
	listLocations,
	listMarketManagers,
	listMarkets,
	listOperators,
	marqueeCurrentOperator,
	searchHosts
} from '@/sdk'

/**
 * Query-options factories for every Marquee read.
 *
 * Each factory colocates its key, its fetcher and the options that are pure
 * functions of its arguments, so `useQuery`, `ensureQueryData`, `prefetchQuery`
 * and `setQueryData` all see the same cached shape. Options driven by external
 * React state (poll intervals, caller-side gating) stay at the call site.
 *
 * SDK calls reject on failure and `response.data` is the payload (never
 * `payload | undefined`) because the generator sets `throwOnError: true` on the
 * client plugin. Never pass it per call; pass `throwOnError: false` only when
 * handling an error inline.
 */

/**
 * The tracker list, fetched whole and unfiltered: `GET /events` takes no cursor
 * and declares `next_cursor` as "currently always null (pagination not yet
 * implemented)", so there is nothing to page through. Tab filtering and search
 * both happen at the call site, which means one cached list serves every tab:
 * switching tabs and typing cost no request.
 */
export const eventListQueryOptions = () =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.events.index(),
		queryFn: async ({ signal }) => {
			const response = await listEvents({ signal })
			return response.data
		}
	})

export const eventQueryOptions = (eventId: string | undefined) =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.events.detail(eventId ?? ''),
		queryFn: async ({ signal }) => {
			const response = await getEvent({
				path: { eventId: eventId ?? '' },
				signal
			})
			return response.data
		},
		enabled: Boolean(eventId)
	})

export const attendeesQueryOptions = (eventId: string | undefined) =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.events.attendees(eventId ?? ''),
		queryFn: async ({ signal }) => {
			const response = await listAttendees({
				path: { eventId: eventId ?? '' },
				signal
			})
			return response.data
		},
		enabled: Boolean(eventId)
	})

/**
 * The Export tab's source of truth: validation gate, preview rows, counts and
 * the last-export summary. Only meaningful once the event is closed, so callers
 * add their own `enabled` for the event state on top of this one.
 */
export const exportPreviewQueryOptions = (eventId: string | undefined) =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.events.exportPreview(eventId ?? ''),
		queryFn: async ({ signal }) => {
			const response = await exportPreview({
				path: { eventId: eventId ?? '' },
				signal
			})
			return response.data
		},
		enabled: Boolean(eventId)
	})

/**
 * Host directory search backing the assign-host fix loop. The query is already
 * debounced by the caller, so each settled term is its own cache entry and
 * retyping an earlier one costs no request.
 *
 * Empty term means no request and no list at all, per design: this search reads
 * a resolver, so it is never a browsable directory. The backend caps the list at
 * 20 and reports no total, so what comes back is what we show.
 */
export const hostSearchQueryOptions = (query: string) =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.hosts.search(query),
		queryFn: async ({ signal }) => {
			const response = await searchHosts({ query: { q: query }, signal })
			return response.data
		},
		enabled: query.length > 0
	})

/**
 * The signed-in operator, and the only place the client learns what that
 * operator may change (FR51). Every screen that owns a creator-only control
 * reads it and hands the operator to [[isMarketManagerAllowed]]; nothing
 * branches on `role`.
 *
 * Fetched once and reused: the answer is a property of the session, so it is
 * held for the whole shift rather than refetched per screen, and it is never
 * invalidated by an event mutation. A dead session answers 401 here, which the
 * SDK's interceptor already turns into a sign-out.
 *
 * **Any failure here is a refusal**, the 404 included: an operator this backend
 * cannot describe is an operator it will not let work, and `MarqueeSessionRoute`
 * stops the whole section rather than letting them into screens where every
 * write fails. Note what that means for a deploy: a backend without
 * `GET /auth/me` (production, until it ships the role partition) closes Marquee
 * for everyone, so the two sides ship together.
 *
 * Retries are off: every answer this can give is a settled one.
 */
export const operatorQueryOptions = () =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.operator.current(),
		queryFn: async ({ signal }) => {
			const response = await marqueeCurrentOperator({ signal })
			return response.data
		},
		staleTime: Number.POSITIVE_INFINITY,
		retry: false
	})

export const marketsQueryOptions = () =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.markets.all(),
		queryFn: async ({ signal }) => {
			const response = await listMarkets({ signal })
			return response.data
		}
	})

export const ctasQueryOptions = () =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.ctas.all(),
		queryFn: async ({ signal }) => {
			const response = await listCtas({ signal })
			return response.data
		}
	})

export const marketManagersQueryOptions = () =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.marketManagers.all(),
		queryFn: async ({ signal }) => {
			const response = await listMarketManagers({
				signal
			})
			return response.data
		}
	})

/**
 * The whole operator roster behind the Team screen (`GET /operators`), active
 * and deactivated, fetched unfiltered like the tracker list: role, status and
 * market-manager filters and the search all run on this one cached list.
 *
 * Admin-only, unlike every other read here, so the page asks the permission
 * (`listOperators`) before it enables this and a non-admin sends no request.
 */
export const rosterQueryOptions = () =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.roster.index(),
		queryFn: async ({ signal }) => {
			const response = await listOperators({ signal })
			return response.data
		}
	})

/**
 * Custom locations (`GET /locations`). The event form reads the active ones;
 * the admin Locations screen passes `includeInactive`, which only an admin may
 * ask for, so that page checks `createLocation` before it enables this.
 * Search and the status filter run on the cached list, as everywhere else.
 */
export const locationsQueryOptions = ({
	includeInactive = false
}: {
	includeInactive?: boolean
} = {}) =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.locations.index({ includeInactive }),
		queryFn: async ({ signal }) => {
			const response = await listLocations({
				query: { include_inactive: includeInactive },
				signal
			})
			return response.data
		}
	})

/**
 * What the host ID to Salesforce account snapshot currently holds: row count
 * and who replaced it last (FR56).
 *
 * It reports the **uploaded** table and nothing else, which is why the panel it
 * feeds has three states rather than two. A non-zero `row_count` with a null
 * `uploaded_at` is not a missing table: it is the list bundled with the deploy,
 * seeded at migration and resolving perfectly well, which no operator has
 * replaced yet. Only `row_count: 0` means nothing resolves.
 */
export const resolutionSourceQueryOptions = () =>
	queryOptions({
		queryKey: makeQueryKeys.marquee.resolutionSource.status(),
		queryFn: async ({ signal }) => {
			const response = await getResolutionSource({ signal })
			return response.data
		}
	})
